import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaQuestionCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getFAQs } from '../firebase/db';
import {
  getLocalizedFAQQuestion,
  getLocalizedFAQAnswer,
  getLocalizedFAQCategory,
  translateFAQText,
  normalizeLang
} from '../utils/faqLocalization';
import './FAQ.css';

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language || 'mr');
  const [faqs, setFAQs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [dynamicTranslations, setDynamicTranslations] = useState({});

  const fallbackFAQs = [
    {
      id: '1',
      category: 'Bedana Processing',
      question: t('faqQ1'),
      answer: t('faqA1')
    },
    {
      id: '2',
      category: 'Washing & Cleaning',
      question: t('faqQ2'),
      answer: t('faqA2')
    },
    {
      id: '3',
      category: 'Custom Equipment',
      question: t('faqQ3'),
      answer: t('faqA3')
    },
    {
      id: '4',
      category: 'Bedana Processing',
      question: t('faqQ4'),
      answer: t('faqA4')
    }
  ];

  useEffect(() => {
    document.title = 'FAQ | Digvijay Industries';
    const fetchFAQs = async () => {
      setLoading(true);
      const data = await getFAQs();
      setFAQs(data);
      setLoading(false);
    };
    fetchFAQs();
  }, []);

  const effectiveFAQs = faqs && faqs.length > 0 ? faqs : fallbackFAQs;

  // Background translation for any newly created dynamic FAQs that don't yet have _mr or _kn
  useEffect(() => {
    if (currentLang === 'en' || !effectiveFAQs || effectiveFAQs.length === 0) return;

    let isMounted = true;
    effectiveFAQs.forEach(async (faq) => {
      const qField = currentLang === 'mr' ? (faq.question_mr || faq.questionMr) : (faq.question_kn || faq.questionKn);
      const aField = currentLang === 'mr' ? (faq.answer_mr || faq.answerMr) : (faq.answer_kn || faq.answerKn);

      if (!qField && faq.question) {
        const transQ = await translateFAQText(faq.question, currentLang);
        if (isMounted && transQ && transQ !== faq.question) {
          setDynamicTranslations(prev => ({ ...prev, [`q_${faq.id}_${currentLang}`]: transQ }));
        }
      }

      if (!aField && faq.answer) {
        const transA = await translateFAQText(faq.answer, currentLang);
        if (isMounted && transA && transA !== faq.answer) {
          setDynamicTranslations(prev => ({ ...prev, [`a_${faq.id}_${currentLang}`]: transA }));
        }
      }
    });

    return () => { isMounted = false; };
  }, [effectiveFAQs, currentLang]);

  const categories = ['All', ...new Set(effectiveFAQs.map(f => f.category).filter(Boolean))];

  const filteredFAQs = effectiveFAQs.filter(f => 
    activeCategory === 'All' || f.category === activeCategory
  );

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const getQuestion = (faq) => {
    const dyn = dynamicTranslations[`q_${faq.id}_${currentLang}`];
    if (dyn) return dyn;
    return getLocalizedFAQQuestion(faq, currentLang);
  };

  const getAnswer = (faq) => {
    const dyn = dynamicTranslations[`a_${faq.id}_${currentLang}`];
    if (dyn) return dyn;
    return getLocalizedFAQAnswer(faq, currentLang);
  };

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <div className="page-header">
        <div className="banner-particle bp-1" />
        <div className="banner-particle bp-2" />
        <div className="banner-particle bp-3" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="banner-badge">{t('faqBadge')}</span>
            <h1>{t('faqTitle1')} <span className="highlight-red">{t('faqTitle2')}</span></h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            {t('faqSubtitle')}
          </motion.p>
        </div>
      </div>

      {/* FAQ Content */}
      <section className="section bg-light">
        <div className="container">
          {/* Category Filter */}
          <motion.div 
            className="faq-categories"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {categories.map(cat => (
              <button
                key={cat}
                className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {getLocalizedFAQCategory(cat, currentLang, t)}
              </button>
            ))}
          </motion.div>

          {/* FAQ List */}
          {loading ? (
            <div className="loading-container" style={{ padding: '60px 0' }}>
              <div className="spinner"></div>
            </div>
          ) : filteredFAQs.length === 0 ? (
            <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FaQuestionCircle style={{ fontSize: '3rem', color: '#ccc', marginBottom: '20px' }} />
              <p style={{ color: '#666' }}>{t('faqNoFaqs')}</p>
            </div>
          ) : (
            <div className="faq-list">
              {filteredFAQs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  className="faq-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <button
                    className={`faq-question ${openFAQ === faq.id ? 'open' : ''}`}
                    onClick={() => toggleFAQ(faq.id)}
                  >
                    <span className="faq-question-text">{getQuestion(faq)}</span>
                    {openFAQ === faq.id ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  <div className={`faq-answer ${openFAQ === faq.id ? 'open' : ''}`}>
                    <p>{getAnswer(faq)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Contact CTA */}
          <motion.div 
            className="faq-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <h3>{t('faqStillQuestions')}</h3>
            <p>{t('faqStillQuestionsDesc')}</p>
            <a href="/contact" className="btn btn-primary">{t('faqContactBtn')}</a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
